import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
// Helpers
import {
  clearMatchupInviteTokenFromLocalStorage,
  getMatchupInviteTokenFromLocalStorage,
} from 'helpers/storage';
import { isStoreRole } from 'helpers/user';
// Constants
import { JOIN_MATCHUP } from 'constants/routes';
// Hooks
import { useGetCurrUser } from 'hooks';
import { useAcceptMatchupInvite } from 'hooks/useAcceptMatchupInvite';
// Ui
import Loader from 'ui3/Loader/Loader';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './MatchupInviteWatcher.module.scss';

// Accepts the invite saved by /join once onboarding is done, covering the page
// until the stream page opens.
const MatchupInviteWatcher = () => {
  const { pathname, push } = useRouter();
  const { data } = useGetCurrUser();
  const { acceptMatchupInvite } = useAcceptMatchupInvite();
  const hasFiredRef = useRef(false);
  const isMountedRef = useRef(true);
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    const user = data?.me;
    const isOnboardedStoreUser =
      !!user && isStoreRole(user.role) && user.isOnboarded;
    const inviteToken = getMatchupInviteTokenFromLocalStorage();

    // While mounted, /join accepts the invite itself.
    if (
      hasFiredRef.current ||
      !inviteToken ||
      !isOnboardedStoreUser ||
      pathname === JOIN_MATCHUP
    ) {
      return;
    }

    hasFiredRef.current = true;
    setIsAccepting(true);

    acceptMatchupInvite(inviteToken)
      .then(
        (streamPath) => {
          clearMatchupInviteTokenFromLocalStorage();
          // Returned so the loader stays until the stream page loads.
          return push(streamPath);
        },
        (error) => {
          clearMatchupInviteTokenFromLocalStorage();
          showToast({
            message: error?.message || 'Could not accept the invite.',
            type: 'error',
          });
        }
      )
      .finally(() => {
        if (isMountedRef.current) {
          setIsAccepting(false);
        }
      });
  }, [data, pathname, acceptMatchupInvite, push]);

  if (!isAccepting) {
    return null;
  }

  return createPortal(
    <div className={styles.overlay}>
      <Loader loading size="xxLarge" color="harvest-gold" />
    </div>,
    document.body
  );
};

export default MatchupInviteWatcher;
